import { useEffect, useState } from 'react';
import { DESKTOP_MIN_WIDTH_QUERY } from '@buzzmap/shared';

/**
 * useMediaQuery - reactive hook that returns whether a CSS media query matches.
 *
 * SSR-safe: returns `false` when `window` is undefined.
 * Updates on viewport changes via `matchMedia.addEventListener('change', ...)`.
 *
 * @param query - A standard CSS media query string, e.g. '(min-width: 1024px)'.
 * @returns boolean reactive to changes of the media query match state.
 */
export function useMediaQuery(query: string): boolean {
  const getMatch = (): boolean => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(query).matches;
  };

  const [matches, setMatches] = useState<boolean>(getMatch);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    const mediaQueryList = window.matchMedia(query);

    // Sync state in case query changed between initial render and effect run.
    setMatches(mediaQueryList.matches);

    const handler = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    mediaQueryList.addEventListener('change', handler);

    return () => {
      mediaQueryList.removeEventListener('change', handler);
    };
  }, [query]);

  return matches;
}

/**
 * useIsDesktop - convenience wrapper around useMediaQuery for desktop breakpoint.
 *
 * Breakpoint: `(min-width: 1024px)`, from `DESKTOP_MIN_WIDTH_QUERY` (packages/shared): the Home
 * hero-map preload (T205) uses the complement of this very query as its `media`.
 */
export function useIsDesktop(): boolean {
  return useMediaQuery(DESKTOP_MIN_WIDTH_QUERY);
}
